/* ============================================================
   CashManagement.jsx
   ------------------------------------------------------------
   Responsibilities:
   - Daily cash operations with single/range date filtering
   - Financial summary
   - Inventory & sales person filtering
   - Cash movement logging
   ------------------------------------------------------------
   APIs:
   - GET /cash/stats
   - GET /cash/movements
   - GET /sales/cashh (external API for totals with date range)
   - GET /cashman (cash management summary)
   ============================================================ */

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';

import { userContext } from '../../context/ContextProvider';
import cashService from '../../services/cashService';

import CashCountModal from './CashCountModal';
import ChequeModal from './ChequeModal';
import CollectionModal from './CollectionModal';
import CreditCollectionModal from './CreditCollectionModal';
import ExpenseModal from './ExpenseModal';
import CashMovementTable from './CashMovementTable';
import ConfirmDialog from '../common/ConfirmDialog';

import { showNotification } from '../../utils/notification';
import './cashManagement.css';

/* ============================================================
   Helpers
   ============================================================ */

const formatCurrency = (value) => {
  const num = Number(value || 0);
  return `Rs.${num.toLocaleString()}`;
};

const getTodayDate = () => {
  return new Date().toISOString().split('T')[0];
};

/* ============================================================
   Component
   ============================================================ */

const CashManagement = ({ toggleSidebar }) => {
  /* ----------------------------------------------------------
     Context
     ---------------------------------------------------------- */
  const { role, user } = useContext(userContext);
  const userRole = role;
  const userId = user?.id || user?.userId;

  /* ----------------------------------------------------------
     Filters
     ---------------------------------------------------------- */
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [selectedToDate, setSelectedToDate] = useState(getTodayDate());
  const [selectedInventory, setSelectedInventory] = useState('ALL');
  const [selectedSalesPerson, setSelectedSalesPerson] = useState('ALL');
  const [isDateRangeMode, setIsDateRangeMode] = useState(false);

  /* ----------------------------------------------------------
     Master data
     ---------------------------------------------------------- */
  const [inventories, setInventories] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);

  /* ----------------------------------------------------------
     UPDATED: cashManagementData now includes all API fields
     ---------------------------------------------------------- */
  const [cashManagementData, setCashManagementData] = useState({
    summary: {
      totalCash: 0,
      totalCollection: 0,
      averageCash: 0,
      averageCollection: 0,
      totalRecords: 0,
      totalChequeAmount: 0,
      totalChequeCount: 0,
      totalExpenseAmount: 0,
      totalExpenseCount: 0,
      grandTotal: 0,
    },
    details: {
      cheques: [],
      expenses: [],
    },
    CASH:         { total: 0 },
    CHEQUE:       { total: 0 },
    CREDIT:       { total: 0 },
    RETURN:       { total: 0 },
    TOTAL_SALES:  { count: 0, value: 0 },
    TOTAL_COST:   { total: 0 },
    GROSS_PROFIT: { total: 0 },
    COLLECTIONS:  { cash: 0, cheque: 0, total: 0 },
  });

  /* ----------------------------------------------------------
     Cash data
     ---------------------------------------------------------- */
  const [movements, setMovements] = useState([]);
  const [stats, setStats] = useState({
    systemCash: 0,
    actualCash: 0,
    totalExpenses: 0,
    totalReturns: 0,
    totalCollection: 0,
    totalCheques: 0,
    cashMissing: 0,
    totalToday: 0,
  });

  const [cashMovementTotals, setCashMovementTotals] = useState({
    CASH: { totalAmount: 0, count: 0 },
    CHEQUE: { totalAmount: 0, count: 0 },
    COLLECTION: { totalAmount: 0, count: 0 },
    ALL: { totalAmount: 0, count: 0 },
  });

  /* ----------------------------------------------------------
     UI state
     ---------------------------------------------------------- */
  const [isLoading, setIsLoading] = useState(false);

  /* ----------------------------------------------------------
     Modal states
     ---------------------------------------------------------- */
  const [isCashCountModalOpen, setIsCashCountModalOpen] = useState(false);
  const [isChequeModalOpen, setIsChequeModalOpen] = useState(false);
  const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);
  const [isCreditCollectionModalOpen, setIsCreditCollectionModalOpen] = useState(false);
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);

  /* ----------------------------------------------------------
     Delete confirmation
     ---------------------------------------------------------- */
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [movementToDelete, setMovementToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /* ----------------------------------------------------------
     Draft states
     ---------------------------------------------------------- */
  const [draftCashCount, setDraftCashCount] = useState(null);
  const [draftCheque, setDraftCheque] = useState(null);
  const [draftCollection, setDraftCollection] = useState(null);
  const [draftExpense, setDraftExpense] = useState(null);

  /* ============================================================
     Data loading
     ============================================================ */

  const loadStats = useCallback(async () => {
    try {
      const effectiveToDate = isDateRangeMode ? selectedToDate : selectedDate;
      const inventoryId = selectedInventory === 'ALL' ? null : Number(selectedInventory);
      const salesPersonId = selectedSalesPerson === 'ALL' ? null : Number(selectedSalesPerson);

      const payload = await cashService.getStats(
        selectedDate,
        effectiveToDate,
        userRole,
        userId,
        inventoryId,
        salesPersonId
      );

      const totalReturns = await cashService.getReturns(
        selectedDate,
        selectedInventory
      );

      setStats({
        ...(payload?.stats || {}),
        totalReturns,
      });

      setInventories(payload?.inventories || []);
      setSalesPersons(payload?.salesPersons || []);
    } catch (error) {
      console.error('Failed to load stats:', error);
      showNotification('Failed to load statistics', 'error');
    }
  }, [selectedDate, selectedToDate, isDateRangeMode, selectedInventory, selectedSalesPerson, userRole, userId]);

  const loadMovements = useCallback(async () => {
    try {
      const effectiveToDate = isDateRangeMode ? selectedToDate : selectedDate;

      const res = await cashService.getMovements(
        selectedDate,
        effectiveToDate,
        userRole,
        userId
      );

      const list = Array.isArray(res) ? res : res?.movements || [];
      setMovements(list);
    } catch (error) {
      console.error('Failed to load movements:', error);
      showNotification('Failed to load movements', 'error');
    }
  }, [selectedDate, selectedToDate, isDateRangeMode, userRole, userId]);

  const loadCashMovementTotals = useCallback(async () => {
    try {
      if (!inventories || inventories.length === 0) {
        return;
      }

      let inventoryIdToUse;
      if (selectedInventory === 'ALL') {
        inventoryIdToUse = inventories[0]?.id;
      } else {
        inventoryIdToUse = Number(selectedInventory);
      }

      if (!inventoryIdToUse) {
        return;
      }

      let salesPersonIdToUse;
      if (selectedSalesPerson === 'ALL') {
        salesPersonIdToUse = userId || 1;
      } else {
        salesPersonIdToUse = Number(selectedSalesPerson);
      }

      if (!salesPersonIdToUse || salesPersonIdToUse === 0) {
        salesPersonIdToUse = 1;
      }

      const effectiveToDate = isDateRangeMode ? selectedToDate : selectedDate;

      const totals = await cashService.getCashMovementTotals(
        inventoryIdToUse,
        salesPersonIdToUse,
        selectedDate,
        effectiveToDate
      );

      setCashMovementTotals(totals);
    } catch (error) {
      console.error('Failed to load cash movement totals:', error);
      showNotification('Failed to load cash movement totals', 'error');
    }
  }, [selectedInventory, selectedSalesPerson, selectedDate, selectedToDate, userId, inventories, isDateRangeMode]);

  /* ----------------------------------------------------------
     UPDATED: loadCashManagementData maps all new API fields
     ---------------------------------------------------------- */
  const loadCashManagementData = useCallback(async () => {
    try {
      if (selectedInventory === 'ALL') {
        return;
      }

      const inventoryIdToUse = Number(selectedInventory);
      const endDate = isDateRangeMode ? selectedToDate : null;

      const data = await cashService.getCashManagementData(
        inventoryIdToUse,
        selectedDate,
        endDate
      );

      if (data) {
        // The API returns { success, summary: { period, CASH, CHEQUE, ... } }
        // We pull from data.summary (API root summary) or data directly
        const apiSummary = data.summary || data;

        setCashManagementData(prev => ({
          ...prev,
          // Keep legacy summary/details if present
          ...(data.summary && data.details
            ? { summary: data.summary, details: data.details }
            : {}),
          // Map all new fields from API response
          CASH:         apiSummary.CASH         || { total: 0 },
          CHEQUE:       apiSummary.CHEQUE       || { total: 0 },
          CREDIT:       apiSummary.CREDIT       || { total: 0 },
          RETURN:       apiSummary.RETURN       || { total: 0 },
          TOTAL_SALES:  apiSummary.TOTAL_SALES  || { count: 0, value: 0 },
          TOTAL_COST:   apiSummary.TOTAL_COST   || { total: 0 },
          GROSS_PROFIT: apiSummary.GROSS_PROFIT || { total: 0 },
          COLLECTIONS:  apiSummary.COLLECTIONS  || { cash: 0, cheque: 0, total: 0 },
        }));
      }
    } catch (error) {
      console.error('Failed to load cash management data:', error);
    }
  }, [selectedDate, selectedToDate, selectedInventory, isDateRangeMode]);

  const loadAllData = useCallback(async () => {
    setIsLoading(true);
    try {
      await Promise.all([loadStats(), loadMovements()]);
    } catch (err) {
      console.error('Failed to load cash data', err);
      showNotification('Failed to load cash data', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [loadStats, loadMovements]);

  // Initial load
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Reload totals and management data when filters change
  useEffect(() => {
    if (inventories.length > 0) {
      loadCashMovementTotals();
      loadCashManagementData();
    }
  }, [inventories.length, selectedInventory, selectedSalesPerson, selectedDate, selectedToDate, isDateRangeMode]);

  /* ============================================================
     Draft Handlers
     ============================================================ */

  const handleSaveCashCount = async (payload) => {
    setDraftCashCount(payload);
    setIsCashCountModalOpen(false);
  };

  const handleSaveCheque = async (payload) => {
    setDraftCheque(payload);
    setIsChequeModalOpen(false);
  };

  const handleSaveCollection = async (payload) => {
    setDraftCollection(payload);
    setIsCollectionModalOpen(false);
  };

  const handleSaveExpense = async (payload) => {
    setDraftExpense(payload);
    setIsExpenseModalOpen(false);
  };

  const handleSaveCreditCollection = async (payload) => {
    try {
      await cashService.addCreditCollection(payload);
      showNotification('Credit collection saved successfully', 'success');
      setIsCreditCollectionModalOpen(false);
      await loadAllData();
    } catch (error) {
      console.error('Credit collection save failed:', error);
      showNotification(
        error?.response?.data?.message || 'Failed to save credit collection',
        'error'
      );
      throw error;
    }
  };

  /* ============================================================
     Filtering (frontend)
     ============================================================ */

  const filteredMovements = useMemo(() => {
    return movements
      .filter((m) => {
        const movementDate = m.date?.split('T')[0];

        let dateMatch;
        if (isDateRangeMode) {
          dateMatch = movementDate >= selectedDate && movementDate <= selectedToDate;
        } else {
          dateMatch = movementDate === selectedDate;
        }

        const inventoryMatch =
          selectedInventory === 'ALL' ||
          m.inventoryId === Number(selectedInventory);

        const salesPersonMatch =
          selectedSalesPerson === 'ALL' ||
          m.salesPersonId === Number(selectedSalesPerson);

        return dateMatch && inventoryMatch && salesPersonMatch;
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [movements, selectedDate, selectedToDate, selectedInventory, selectedSalesPerson, isDateRangeMode]);

  const handleClearFilters = () => {
    const today = getTodayDate();
    setSelectedDate(today);
    setSelectedToDate(today);
    setSelectedInventory('ALL');
    setSelectedSalesPerson('ALL');
    setIsDateRangeMode(false);
  };

  const handleToggleDateRange = () => {
    const newMode = !isDateRangeMode;
    setIsDateRangeMode(newMode);
    if (!newMode) {
      setSelectedToDate(selectedDate);
    }
  };

  /* ============================================================
     Delete handlers
     ============================================================ */

  const handleDeleteClick = (movement) => {
    setMovementToDelete(movement);
    setIsConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!movementToDelete) return;

    try {
      setIsDeleting(true);
      await cashService.deleteMovement(movementToDelete.id);
      showNotification('Movement deleted successfully', 'success');
      await loadAllData();
    } catch (err) {
      console.error(err);
      showNotification('Failed to delete movement', 'error');
    } finally {
      setIsDeleting(false);
      setIsConfirmOpen(false);
      setMovementToDelete(null);
    }
  };

  /* ============================================================
     Save All Cash Management Records with Rollback
     ============================================================ */

  const handleSaveAllCashManagement = async () => {
    const createdRecords = {
      chequeIds: [],
      expenseIds: [],
    };

    try {
      setIsLoading(true);

      if (!draftCheque?.length && !draftExpense?.length && !draftCashCount && !draftCollection) {
        showNotification('No data to save. Please add cheques, expenses, cash count, or collections first.', 'warning');
        return;
      }

      if (selectedInventory === 'ALL') {
        showNotification('Please select a specific inventory before saving', 'warning');
        return;
      }

      // Step 1: Save Cheques
      if (draftCheque && draftCheque.length > 0) {
        for (const cheque of draftCheque) {
          try {
            const { id, customerName, ...chequeData } = cheque;
            const response = await cashService.addCheque(chequeData);

            const chequeId =
              response?.data?.[0]?.id ||
              response?.data?.id ||
              response?.id;

            if (chequeId) {
              createdRecords.chequeIds.push(chequeId);
            } else {
              await rollbackCreatedRecords(createdRecords);
              throw new Error('Failed to extract cheque ID from API response');
            }
          } catch (error) {
            await rollbackCreatedRecords(createdRecords);
            throw new Error(`Failed to save cheque: ${error?.response?.data?.message || error.message}`);
          }
        }
      }

      // Step 2: Save Expenses
      if (draftExpense && draftExpense.length > 0) {
        for (const expense of draftExpense) {
          try {
            const { id, ...expenseData } = expense;
            const response = await cashService.addExpense(expenseData);

            const expenseId =
              response?.data?.[0]?.id ||
              response?.data?.id ||
              response?.id;

            if (expenseId) {
              createdRecords.expenseIds.push(expenseId);
            } else {
              await rollbackCreatedRecords(createdRecords);
              throw new Error('Failed to extract expense ID from API response');
            }
          } catch (error) {
            await rollbackCreatedRecords(createdRecords);
            throw new Error(`Failed to save expense: ${error?.response?.data?.message || error.message}`);
          }
        }
      }

      // Step 3: Create final cash management record
      const cashManagementPayload = {
        cashCount: draftCashCount?.totalAmount || 0,
        collectionCount: draftCollection?.amount || 0,
        cheques: createdRecords.chequeIds,
        expenses: createdRecords.expenseIds,
        inventoryId: parseInt(selectedInventory),
        salesPersonId:
          selectedSalesPerson !== 'ALL' ? parseInt(selectedSalesPerson) : userId,
        description: generateDescription(
          createdRecords.chequeIds.length,
          createdRecords.expenseIds.length,
          draftCashCount,
          draftCollection
        ),
        transactionDate: new Date(selectedDate).toISOString(),
        paymentMethod: determinePaymentMethod(
          draftCashCount,
          draftCollection,
          createdRecords.chequeIds
        ),
        referenceNumber: generateReferenceNumber(selectedDate),
      };

      try {
        await cashService.createCashManagement(cashManagementPayload);

        setDraftCheque(null);
        setDraftExpense(null);
        setDraftCashCount(null);
        setDraftCollection(null);

        await loadAllData();

        showNotification(
          `Successfully saved: ${createdRecords.chequeIds.length} cheques, ${createdRecords.expenseIds.length} expenses, cash management record created!`,
          'success'
        );
      } catch (finalError) {
        await rollbackCreatedRecords(createdRecords);
        throw new Error('Failed to create cash management record. All changes have been rolled back.');
      }
    } catch (error) {
      console.error('Failed to save cash management:', error);
      showNotification(
        error?.message || error?.response?.data?.message || 'Failed to save cash management records',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  /* ============================================================
     Rollback Function
     ============================================================ */

  const rollbackCreatedRecords = async (createdRecords) => {
    const rollbackErrors = [];

    if (createdRecords.chequeIds.length > 0) {
      for (const chequeId of createdRecords.chequeIds) {
        try {
          await cashService.deleteCheque(chequeId);
        } catch (error) {
          rollbackErrors.push(`cheque ${chequeId}`);
        }
      }
    }

    if (createdRecords.expenseIds.length > 0) {
      for (const expenseId of createdRecords.expenseIds) {
        try {
          await cashService.deleteExpense(expenseId);
        } catch (error) {
          rollbackErrors.push(`expense ${expenseId}`);
        }
      }
    }

    if (rollbackErrors.length > 0) {
      showNotification(
        `Rollback partially failed. Please manually check: ${rollbackErrors.join(', ')}`,
        'warning'
      );
    } else {
      showNotification(
        'Transaction failed. All changes have been rolled back successfully.',
        'info'
      );
    }
  };

  /* ============================================================
     Helper Functions
     ============================================================ */

  const generateDescription = (chequeCount, expenseCount, cashCount, collection) => {
    const parts = [];
    if (chequeCount > 0) parts.push(`${chequeCount} cheque deposit${chequeCount > 1 ? 's' : ''}`);
    if (expenseCount > 0) parts.push(`${expenseCount} expense payment${expenseCount > 1 ? 's' : ''}`);
    if (cashCount) parts.push('cash count recorded');
    if (collection) parts.push('collection recorded');

    const description =
      parts.length > 0
        ? `Complete daily transaction: ${parts.join(', ')}`
        : 'Daily cash management transaction';

    const salesPersonInfo =
      selectedSalesPerson !== 'ALL'
        ? `, handled by sales person #${selectedSalesPerson}`
        : '';

    return description + salesPersonInfo;
  };

  const determinePaymentMethod = (cashCount, collection, chequeIds) => {
    const methods = [];
    if (cashCount && cashCount.totalAmount > 0) methods.push('cash');
    if (collection && collection.amount > 0) methods.push('collection');
    if (chequeIds.length > 0) methods.push('cheque');

    if (methods.length === 0) return 'none';
    if (methods.length === 1) return methods[0];
    return 'mixed';
  };

  const generateReferenceNumber = (date) => {
    const dateObj = new Date(date);
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = dateObj.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const year = dateObj.getFullYear();
    const timestamp = Date.now().toString().slice(-4);
    return `EOD-${day}-${month}-${year}-${timestamp}`;
  };

  const hasUnsavedData = () => {
    return (
      (draftCheque && draftCheque.length > 0) ||
      (draftExpense && draftExpense.length > 0) ||
      draftCashCount !== null ||
      draftCollection !== null
    );
  };

  /* ============================================================
     Render
     ============================================================ */

  return (
    <main className="inventory-main-content" style={{ padding: '3px 12px 30px 30px' }}>
      <style>{`
        .cash-actions button {
          padding: 10px 20px;
          font-size: 14px;
          font-weight: 600;
          color: #ffffff;
          background: linear-gradient(135deg, #173a85 0%, #1d4ed8 100%);
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.3);
        }
        .cash-actions button:hover {
          background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.4);
          transform: translateY(-2px);
        }
        .cash-actions button:active {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(37, 99, 235, 0.3);
        }
        .cash-actions button:disabled {
          background: #94a3b8;
          cursor: not-allowed;
          box-shadow: none;
          opacity: 0.6;
        }
      `}</style>

      {/* Header & Filters */}
      <header className="inventory-content-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={toggleSidebar} className="sidebar-toggle-btn">☰</button>
          <h2 className="inventory-content-title">Cash Management</h2>
        </div>

        <div className="header-actions">
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            padding: '16px',
            backgroundColor: '#f9fafb',
            borderRadius: '10px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
            marginBottom: '24px',
            alignItems: 'flex-end',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: '180px', gap: '6px' }}>
              <label style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '2px' }}>
                {isDateRangeMode ? 'From Date' : 'Date'}
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  if (!isDateRangeMode) setSelectedToDate(e.target.value);
                }}
                style={{
                  padding: '10px 12px', fontSize: '14px', border: '1px solid #d1d5db',
                  borderRadius: '8px', outline: 'none', backgroundColor: '#ffffff',
                  color: '#111827', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', cursor: 'pointer',
                }}
              />
            </div>

            {isDateRangeMode && (
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: '180px', gap: '6px' }}>
                <label style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '2px' }}>
                  To Date
                </label>
                <input
                  type="date"
                  value={selectedToDate}
                  onChange={(e) => setSelectedToDate(e.target.value)}
                  style={{
                    padding: '10px 12px', fontSize: '14px', border: '1px solid #d1d5db',
                    borderRadius: '8px', outline: 'none', backgroundColor: '#ffffff',
                    color: '#111827', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', cursor: 'pointer',
                  }}
                />
              </div>
            )}

            <div className="filter-group" style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: '0', margin: '0' }}>
              <label>&nbsp;</label>
              <button
                type="button"
                className={`date-range-toggle-btn ${isDateRangeMode ? 'active' : ''}`}
                onClick={handleToggleDateRange}
                style={{
                  background: isDateRangeMode
                    ? 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)'
                    : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                  color: '#ffffff', padding: '8px 12px', borderRadius: '8px', border: 'none',
                  outline: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
                  height: '38px', display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', whiteSpace: 'nowrap', transition: 'all 0.2s ease',
                }}
              >
                {isDateRangeMode ? '📅 Range Mode' : '📆 Single Date'}
              </button>
            </div>

            <div className="filter-group" style={{
              display: 'flex', flexDirection: 'column', minWidth: '200px', gap: '6px',
              padding: '0', background: 'transparent', border: 'none', boxShadow: 'none', alignItems: 'flex-start',
            }}>
              <label style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '2px' }}>
                Inventory
              </label>
              <select
                value={selectedInventory}
                onChange={(e) => setSelectedInventory(e.target.value)}
                style={{
                  minWidth: '200px', height: '40px', padding: '0 12px', fontSize: '14px',
                  border: '1px solid #d1d5db', borderRadius: '8px', backgroundColor: '#ffffff',
                  color: '#111827', outline: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', cursor: 'pointer',
                }}
              >
                <option value="ALL">All Inventories</option>
                {inventories.map((inv) => (
                  <option key={inv.id} value={inv.id}>{inv.name}</option>
                ))}
              </select>
            </div>

            <div className="filter-group" style={{
              display: 'flex', flexDirection: 'column', gap: '6px', padding: '0',
              background: 'transparent', border: 'none', boxShadow: 'none',
              alignItems: 'flex-start', justifyContent: 'flex-end', minWidth: 'fit-content',
            }}>
              <label style={{ fontSize: '14px', fontWeight: '600', color: 'transparent', marginBottom: '2px', userSelect: 'none' }}>
                Action
              </label>
              <button
                type="button"
                className="clear-filters-btn"
                onClick={handleClearFilters}
                style={{
                  height: '40px', padding: '0 14px', borderRadius: '8px', border: '1px solid #d1d5db',
                  background: '#ffffff', color: '#374151', fontSize: '13px', fontWeight: '600',
                  cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', outline: 'none',
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Actions */}
      <div className="cash-actions">
        <button onClick={() => setIsCashCountModalOpen(true)}>+ Cash Count</button>
        <button onClick={() => setIsChequeModalOpen(true)}>+ Cheque</button>
        <button onClick={() => setIsCollectionModalOpen(true)}>+ Collection</button>
        <button onClick={() => setIsExpenseModalOpen(true)}>+ Expense</button>
      </div>

      {/* Financial Summary */}
      <div className="financial-summary">
        <h2>
          Financial Summary
          {isDateRangeMode
            ? ` (${selectedDate} to ${selectedToDate})`
            : ` (${selectedDate})`}
        </h2>

        <div className="summary-grid">

          {/* ── System / Actual Cash ── */}
          <div className="summary-card">
            <span>System In Cash</span>
            <strong>{formatCurrency(cashMovementTotals.CASH.totalAmount)}</strong>
            <small>{cashMovementTotals.CASH.count} transactions</small>
          </div>

          {/* <div
            className="summary-card clickable"
            onClick={() => { if (draftCashCount) setIsCashCountModalOpen(true); }}
          >
            <span>Actual Cash</span>
            <strong>
              Rs. {draftCashCount
                ? draftCashCount.totalAmount.toLocaleString()
                : cashManagementData.summary.totalCash.toLocaleString()}
            </strong>
            {draftCashCount
              ? <small className="draft-label">Draft – Click to edit</small>
              : <small>From recorded transactions</small>}
          </div> */}

          {/* ── Cash Missing / Surplus ── */}
          {/* <div className={`summary-card ${(() => {
            const sys = cashMovementTotals.CASH.totalAmount;
            const act = draftCashCount ? draftCashCount.totalAmount : cashManagementData.summary.totalCash;
            const diff = sys - act;
            if (diff > 0) return 'warning';
            if (diff < 0) return 'info';
            return 'success';
          })()}`}>
            <span>
              {(() => {
                const sys = cashMovementTotals.CASH.totalAmount;
                const act = draftCashCount ? draftCashCount.totalAmount : cashManagementData.summary.totalCash;
                const diff = sys - act;
                if (diff > 0) return 'Cash Missing';
                if (diff < 0) return 'Cash Surplus';
                return 'Cash Balanced';
              })()}
            </span>
            <strong>
              {(() => {
                const sys = cashMovementTotals.CASH.totalAmount;
                const act = draftCashCount ? draftCashCount.totalAmount : cashManagementData.summary.totalCash;
                return formatCurrency(Math.abs(sys - act));
              })()}
            </strong>
            <small>
              {(() => {
                const sys = cashMovementTotals.CASH.totalAmount;
                const act = draftCashCount ? draftCashCount.totalAmount : cashManagementData.summary.totalCash;
                const diff = sys - act;
                if (diff > 0) return '⚠️ Shortage detected';
                if (diff < 0) return '✅ Excess cash';
                return '✅ Perfect match';
              })()}
            </small>
          </div> */}

          {/* ── Expenses ── */}
          {/* <div
            className="summary-card expense clickable"
            onClick={() => { if (draftExpense) setIsExpenseModalOpen(true); }}
          >
            <span>Total Expenses</span>
            <strong>
              {draftExpense
                ? `Rs. ${draftExpense.reduce((sum, e) => sum + e.amount, 0).toLocaleString()}`
                : formatCurrency(cashManagementData.summary.totalExpenseAmount)}
            </strong>
            {draftExpense
              ? <small className="draft-label">Draft – Click to edit ({draftExpense.length} items)</small>
              : <small>{cashManagementData.summary.totalExpenseCount} expenses recorded</small>}
          </div> */}

          {/* ── System / Actual Collections ── */}
          <div className="summary-card">
            {/* <span>System In Collections</span> */}
            <span>Collections</span>
            <strong>{formatCurrency(cashManagementData.COLLECTIONS.total)}</strong>
            <small>{cashMovementTotals.COLLECTION.count} transactions</small>
          </div>
          

          {/* <div
            className="summary-card clickable"
            onClick={() => { if (draftCollection) setIsCollectionModalOpen(true); }}
          >
            <span>Actual Collection</span>
            <strong>
              Rs. {draftCollection
                ? draftCollection.amount.toLocaleString()
                : cashManagementData.summary.totalCollection.toLocaleString()}
            </strong>
            {draftCollection
              ? <small className="draft-label">Draft – Click to edit</small>
              : <small>From recorded transactions</small>}
          </div> */}
          {/* COLLECTIONS breakdown */}
          {/* <div className="summary-card">
            <span>Collections (Cash)</span>
            <strong>{formatCurrency(cashManagementData.COLLECTIONS.cash)}</strong>
          </div>

          <div className="summary-card">
            <span>Collections (Cheque)</span>
            <strong>{formatCurrency(cashManagementData.COLLECTIONS.cheque)}</strong>
          </div> */}

          {/* <div className="summary-card total">
            <span>Collections Total</span>
            <strong>{formatCurrency(cashManagementData.COLLECTIONS.total)}</strong>
          </div> */}

          {/* ── System / Actual Cheques ── */}
          <div className="summary-card">
            <span>System In Cheques</span>
            <strong>{formatCurrency(cashMovementTotals.CHEQUE.totalAmount)}</strong>
            <small>{cashMovementTotals.CHEQUE.count} transactions</small>
          </div>

          {/* <div
            className="summary-card clickable"
            onClick={() => { if (draftCheque) setIsChequeModalOpen(true); }}
          >
            <span>Actual Cheque</span>
            <strong>
              Rs. {draftCheque && draftCheque.length > 0
                ? draftCheque.reduce((sum, c) => sum + c.amount, 0).toLocaleString()
                : Number(cashManagementData.summary.totalChequeAmount).toLocaleString()}
            </strong>
            {draftCheque && draftCheque.length > 0
              ? <small className="draft-label">Draft – Click to edit ({draftCheque.length} cheques)</small>
              : <small>{cashManagementData.summary.totalChequeCount} cheques recorded</small>}
          </div> */}

          {/* ════════════════════════════════════════
              NEW CARDS — from /cashman API response
              ════════════════════════════════════════ */}

          {/* CASH (from cashman) */}
          {/* <div className="summary-card">
            <span>Cash Sales</span>
            <strong>{formatCurrency(cashManagementData.CASH.total)}</strong>
          </div> */}

          {/* CHEQUE (from cashman) */}
          {/* <div className="summary-card">
            <span>Cheque Sales</span>
            <strong>{formatCurrency(cashManagementData.CHEQUE.total)}</strong>
          </div> */}

          {/* CREDIT */}
          <div className="summary-card">
            <span>Credit Sales</span>
            <strong>{formatCurrency(cashManagementData.CREDIT.total)}</strong>
          </div>

          {/* RETURN */}
          <div className="summary-card warning">
            <span>Returns</span>
            <strong>{formatCurrency(cashManagementData.RETURN.total)}</strong>
          </div>

          {/* TOTAL SALES */}
          <div className="summary-card">
            <span>Total Sales</span>
            <strong>{formatCurrency(cashManagementData.TOTAL_SALES.value)}</strong>
            <small>{cashManagementData.TOTAL_SALES.count} transactions</small>
          </div>

          {/* TOTAL COST */}
          <div className="summary-card">
            <span>Total Cost</span>
            <strong>{formatCurrency(cashManagementData.TOTAL_COST.total)}</strong>
          </div>

          {/* GROSS PROFIT */}
          <div className="summary-card success">
            <span>Gross Profit</span>
            <strong>{formatCurrency(cashManagementData.GROSS_PROFIT.total)}</strong>
            <small>
              {cashManagementData.TOTAL_SALES.value > 0
                ? `${((cashManagementData.GROSS_PROFIT.total / cashManagementData.TOTAL_SALES.value) * 100).toFixed(1)}% margin`
                : '—'}
            </small>
          </div>

          

          {/* ── Overall totals (existing) ── */}
          {/* <div className="summary-card total">
            <span>Total Returns</span>
            <strong>{stats.totalReturns}</strong>
          </div> */}

          {/* <div className="summary-card total">
            <span>Total {isDateRangeMode ? 'Period' : 'Today'}</span>
            <strong>{formatCurrency(cashMovementTotals.ALL.totalAmount)}</strong>
            <small>{cashMovementTotals.ALL.count} transactions</small>
          </div> */}

        </div>
      </div>

      {hasUnsavedData() && (
        <div className="save-all-section">
          <div className="save-all-info">
            <h3>⚠️ Unsaved Changes</h3>
            <div className="draft-summary">
              {draftCheque && draftCheque.length > 0 && (
                <span className="draft-item">📋 {draftCheque.length} Cheque{draftCheque.length > 1 ? 's' : ''}</span>
              )}
              {draftExpense && draftExpense.length > 0 && (
                <span className="draft-item">💰 {draftExpense.length} Expense{draftExpense.length > 1 ? 's' : ''}</span>
              )}
              {draftCashCount && (
                <span className="draft-item">💵 Cash Count (Rs. {draftCashCount.totalAmount.toLocaleString()})</span>
              )}
              {draftCollection && (
                <span className="draft-item">📥 Collection (Rs. {draftCollection.amount.toLocaleString()})</span>
              )}
            </div>
          </div>
          <button
            className="save-all-btn"
            onClick={handleSaveAllCashManagement}
            disabled={isLoading}
          >
            {isLoading ? '⏳ Saving All...' : '💾 Save All Cash Management'}
          </button>
        </div>
      )}

      {/* <CashMovementTable
        movements={filteredMovements}
        isLoading={isLoading}
        onDelete={handleDeleteClick}
      /> */}

      <CashCountModal
        isOpen={isCashCountModalOpen}
        onClose={() => setIsCashCountModalOpen(false)}
        onSave={handleSaveCashCount}
        selectedInventory={selectedInventory}
        existingData={draftCashCount}
      />

      <ChequeModal
        isOpen={isChequeModalOpen}
        onClose={() => setIsChequeModalOpen(false)}
        onSave={handleSaveCheque}
        selectedInventory={selectedInventory}
        existingData={draftCheque}
      />

      <CollectionModal
        isOpen={isCollectionModalOpen}
        selectedInventory={selectedInventory}
        onClose={() => setIsCollectionModalOpen(false)}
        onSave={handleSaveCollection}
        existingData={draftCollection}
      />

      <ExpenseModal
        isOpen={isExpenseModalOpen}
        selectedInventory={selectedInventory}
        onClose={() => setIsExpenseModalOpen(false)}
        onSave={handleSaveExpense}
        existingData={draftExpense}
      />

      <ConfirmDialog
        isOpen={isConfirmOpen}
        title="Delete Movement"
        message={`Are you sure you want to delete this ${movementToDelete?.type}?`}
        onConfirm={confirmDelete}
        onCancel={() => setIsConfirmOpen(false)}
        isProcessing={isDeleting}
      />
    </main>
  );
};

export default CashManagement;
